import moment from 'moment'
import { keys } from 'ramda'
import React from 'react'
import { userHasRole } from 'app/core/utils/rbac'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import SimpleLink from 'core/components/SimpleLink'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import { routeManager } from 'core/utils/routes'
import { GPUFlavorTagCellComponent } from 'k8s/components/cluster/deployment/form-components/GPUTag'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import { IVolumeSnapshotDetailsPageTabs } from 'openstack/components/storage/snapshots/details/model'
import { IVmDetailsPageTabs } from 'openstack/components/vms/vm-details/model'
import { humanReadableSize } from 'openstack/helpers'
import { durationBetweenDates } from 'utils/misc'
import { TPMModelText } from '../images/constants'
import ImageStatusInfo from '../images/ImageStatusInfo'
import { TableHeaderTooltip } from '../images/TableHeaderTooltip'
import type { SecurityGroup } from '../networks/model'
import { VolumeSourceCell } from '../storage/volumes/volume-details/VolumeSourceCell'
import { isExplicitPort } from './CreateVirtualMachineModal.helpers'
import { CHANGE_TENANT_VALID_STATES } from './constants'
import { ACTION_LIMITATION_TOOLTIPS } from './tooltipConstants'

interface ExtraDriveInfo {
  volumes: any[]
  hasExtraDrives: boolean
  count: number
}

export const hasOtherUserVm = ({ vms, userId }) => {
  return vms?.some((vm) => vm?.user_id !== userId)
}

export const visibleImageColumns = [
  'name',
  'imageTypeDisplay',
  'disk_format',
  'virtual_size',
  'properties',
  'created_at',
]

export const imageColumnsOrder = [
  'name',
  'id',
  'status',
  'imageTypeDisplay',
  'visibility',
  'protected',
  'disk_format',
  'virtual_size',
  'size',
  'hw_tpm_model',
  'tags',
  'properties',
  'created_at',
]

export const getVmSnapshotColumns = ({ vmsById, volumeSnapshotsById }) => [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'id',
    label: 'UUID',
    display: false,
  },
  {
    id: 'status',
    label: (
      <TitleWithInfoIcon
        titleText="Status"
        tooltipMsg={<ImageStatusInfo entityType={'VM snapshot'} />}
      />
    ),
    display: false,
  },
  {
    id: 'imageTypeDisplay',
    label: 'Type',
  },
  {
    id: 'instance_uuid',
    label: 'Source VM',
    display: false,
    render: (val) =>
      val ? (
        <SimpleLink
          src={routeManager.routes.openstack.vmDetails.path({
            id: val,
            tab: IVmDetailsPageTabs.Overview,
          })}
        >
          {vmsById?.(val)?.name || val}
        </SimpleLink>
      ) : null,
  },
  {
    id: 'snapshotIds',
    label: 'Volume Snapshots',
    display: false,
    render: (val) =>
      val.length ? (
        <div style={{ display: 'grid', gap: '8px' }}>
          {val?.map((snapshotId) => (
            <div key={snapshotId}>
              <SimpleLink
                src={routeManager.routes.openstack.volumeSnapshotDetails.path({
                  id: snapshotId,
                  tab: IVolumeSnapshotDetailsPageTabs.Overview,
                })}
              >
                {volumeSnapshotsById?.[snapshotId]?.name || snapshotId}
              </SimpleLink>
            </div>
          ))}
        </div>
      ) : null,
  },
  {
    id: 'visibility',
    label: 'Visibility',
    display: false,
  },
  {
    id: 'protected',
    label: 'Protected',
    render: (val) => (val ? 'True' : 'False'),
    display: false,
  },
  {
    id: 'disk_format',
    label: 'Disk Format',
  },
  {
    id: 'virtual_size',
    label: (
      <TitleWithInfoIcon
        titleText="Virtual Disk Size"
        tooltipMsg={
          <TableHeaderTooltip message="The size of the VM disk that will get created from this snapshot" />
        }
      />
    ),
    render: humanReadableSize,
  },
  {
    id: 'size',
    label: (
      <TitleWithInfoIcon
        titleText="File Size"
        tooltipMsg={
          <TableHeaderTooltip message="The size of the snapshot file that is stored in the Image Library" />
        }
      />
    ),
    render: humanReadableSize,
    display: false,
  },
  {
    id: 'owner_user_name',
    label: 'Snapshot Owner',
    display: false,
  },
  {
    id: 'hw_tpm_model',
    label: 'Virtual TPM 2.0',
    render: (value, item) =>
      item?.hw_tpm_version === '2.0' ? `Enabled (${TPMModelText[value]})` : '',
    display: false,
  },
  {
    id: 'tags',
    label: 'Tags',
    render: (tags) => tags?.join(', '),
    display: false,
  },
  {
    id: 'created_at',
    label: 'Age',
    render: (value) => durationBetweenDates({ labels: ['d', 'h'] })(value),
  },
]

export const visibleVmSnapshotColumns = [
  'name',
  'imageTypeDisplay',
  'instance_uuid',
  'snapshotIds',
  'disk_format',
  'virtual_size',
  'created_at',
]

export const vmSnapshotColumnsOrder = [
  'name',
  'id',
  'status',
  'imageTypeDisplay',
  'instance_uuid',
  'snapshotIds',
  'visibility',
  'protected',
  'disk_format',
  'virtual_size',
  'size',
  'owner_user_name',
  'hw_tpm_model',
  'tags',
  'created_at',
]

export const volumeColumns = [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'id',
    label: 'UUID',
    display: false,
  },
  {
    id: 'hostName',
    label: 'Host',
    visible: !isAdmin(),
    display: false,
  },
  {
    id: 'backend',
    label: 'Volume Backend',
    visible: !isAdmin(),
    display: false,
  },
  {
    id: 'tenantName',
    label: 'Tenant',
    display: false,
  },
  {
    id: 'status',
    label: 'Status',
  },
  {
    id: 'volume_type',
    label: 'Type',
  },

  {
    id: 'description',
    label: 'Description',
  },
  {
    id: 'device',
    label: 'Device',
    display: false,
  },
  {
    id: 'snapshots',
    label: 'Snapshots',
    render: (snapshots) => snapshots?.length,
    display: false,
  },
  {
    id: 'size',
    label: 'Size',
    render: (size) => humanReadableSize(size * 1024 * 1024 * 1024),
  },
  {
    id: 'bootable',
    label: 'Bootable',
  },
  {
    id: 'multiattach',
    label: 'Multiattach',
    render: (val) => (val ? 'True' : 'False'),
    display: false,
  },
  {
    id: 'source',
    label: (
      <TitleWithInfoIcon
        titleText="Source"
        tooltipMsg={'The resource used to create this volume. Blank if no resource used'}
      />
    ),
    render: (val, item) => <VolumeSourceCell item={item} />,
    display: false,
  },
  {
    id: 'created_at',
    label: 'Age',
    render: (value) => durationBetweenDates({ labels: ['d'] })(value),
  },
  {
    id: 'metadata',
    label: 'Metadata',
    render: (value) => createResourceLabelsCell({ type: 'table', separator: '=' }),
    display: false,
  },
]

export const getGpuInfo = (metadata) => {
  // A flavor uses at most one GPU style: either a passed-through physical GPU
  // ("Passthrough") or a sliced virtual GPU ("vGPU"), never both.
  const passthrough = metadata?.['pci_passthrough:alias']
  const vgpuKey = Object.keys(metadata || {}).find((k) => /^resources\d*:/.test(k))
  const vgpu = vgpuKey ? metadata[vgpuKey] : undefined
  if (passthrough) {
    const [profile, count] = passthrough.split(':')
    return {
      type: 'Passthrough',
      count,
      profile,
    }
  }
  if (vgpu) {
    return {
      type: 'vGPU',
      slices: vgpu,
    }
  }
  return {}
}

export const flavorColumns = [
  {
    id: 'name',
    label: 'Name',
    render: (value, item) => GPUFlavorTagCellComponent({ value, item }),
  },
  {
    id: 'vcpus',
    label: 'vCPUs',
  },
  {
    id: 'ram',
    label: 'RAM',
    render: (ram) => humanReadableSize(ram * 1024 * 1024),
  },
  {
    id: 'disk',
    label: 'Disk',
    render: (disk) => humanReadableSize(disk * 1024 * 1024 * 1024),
  },
]

export const flavorColumnsGpu = [
  ...flavorColumns,
  {
    id: 'gpuFlavor',
    label: 'GPU Support',
    render: (value, item) => {
      const metadata = item?.extra_specs
      const gpuInfo = getGpuInfo(metadata)
      const { type, count, slices } = gpuInfo

      if (!type) return null

      return (
        <div>
          <div>{type}</div>
          {type === 'Passthrough' && count && <div>GPU Count: {count}</div>}
          {type === 'vGPU' && slices && <div>Slices: {slices}</div>}
        </div>
      )
    },
  },
  {
    id: 'extra_specs',
    label: 'Profile',
    render: (value, item) => {
      const metadata = item?.extra_specs
      const { profile } = getGpuInfo(metadata)
      return <div>{profile}</div>
    },
  },
]

export const visibleFlavorColumns = ['name', 'vcpus', 'ram', 'disk', 'extra_specs']

export const visibleGpuFlavorColumns = ['name', 'vcpus', 'ram', 'disk', 'gpuFlavor', 'extra_specs']

export interface SecurityGroupAutoState {
  toggleState: 'free' | 'disabled' | 'lockedOn'
  tooltipMsg: string | undefined
  autoSelectedGroups: SecurityGroup[]
  lockedSecurityGroups: SecurityGroup[]
  // FR-015: true when a chosen existing port brings its own security groups (FR-013 path).
  hasPortSecurityGroups: boolean
}

// FR-011, FR-012, FR-013: Decide how the "Assign Security Groups" control should behave
// for the chosen networks/ports. (A security group is a set of firewall rules; a "port"
// is a VM network interface.) Returns: whether the toggle is off / forced on, a tooltip
// explaining why, which groups to pre-select, and which ones the user can't remove.
//
// PCD-5420 FR-019: when networkAttachments is given, this is judged one NIC row at a time.
// A row that points at a specific existing port pulls in that port's security groups.
// A row left on "Automatic" or "Private IP" on a network that has port security on counts
// as an automatic port (one the system will create), which means a group is needed.
export const getSecurityGroupAutoState = (
  networks: any[],
  selectedPorts: Record<string, string> = {},
  securityGroupsList: SecurityGroup[],
  networkAttachments?: Record<string, { rowId: string; port: string }[]>,
): SecurityGroupAutoState => {
  const empty: SecurityGroupAutoState = {
    toggleState: 'free',
    tooltipMsg: undefined,
    autoSelectedGroups: [],
    lockedSecurityGroups: [],
    hasPortSecurityGroups: false,
  }

  if (!networks?.length) return empty

  // FR-013: Look through every network for security groups that picked ports already
  // carry. We check all networks, not just the secure ones, because a port can have
  // security groups even on a network where port security is turned off.
  const portSgIds = new Set<string>()
  let hasSelectedPortWithSGs = false
  let hasAutomaticPort = false

  for (const network of networks) {
    // FR-019: if this network has NIC rows, read the port chosen on each row; if not,
    // fall back to the older "one port per network" map.
    const rows = networkAttachments?.[network.id]
    const portIds: string[] = rows?.length
      ? rows.map((row) => row.port)
      : [selectedPorts[network.id]]

    for (const portId of portIds) {
      const isExistingPort = !!portId && isExplicitPort(portId)
      if (isExistingPort) {
        const port = network.availablePorts?.find((p: any) => p.id === portId)
        if (port?.security_groups?.length) {
          hasSelectedPortWithSGs = true
          port.security_groups.forEach((sgId: string) => {
            portSgIds.add(sgId)
          })
        }
      } else if (network.port_security_enabled) {
        // This row has no specific port, so the system will create one automatically.
        // On a network with port security on, that automatic port needs a security group.
        hasAutomaticPort = true
      }
    }
  }

  // FR-011: If every chosen network has port security off AND no chosen port brings its
  // own security groups, then security groups don't apply at all — turn the toggle off.
  const allPortSecurityDisabled = networks.every((n) => !n.port_security_enabled)
  if (allPortSecurityDisabled && !hasSelectedPortWithSGs) {
    return {
      ...empty,
      toggleState: 'disabled',
      tooltipMsg: 'Port security is disabled for the selected network(s)',
    }
  }

  // FR-012: Add the "default" security group when the system will create an automatic
  // port, or as a safety net when none of the chosen ports brought any groups of their own.
  const defaultSG = securityGroupsList.find((sg) => sg.name === 'default')
  if (defaultSG && (hasAutomaticPort || !hasSelectedPortWithSGs)) {
    portSgIds.add(defaultSG.id)
  }

  const autoSelectedGroups = securityGroupsList.filter((sg) => portSgIds.has(sg.id))

  // FR-013: Work out which groups the user is not allowed to remove (locked). Only
  // applies when a chosen port forces some groups on us.
  let lockedSecurityGroups: SecurityGroup[] = []
  if (hasSelectedPortWithSGs && portSgIds.size > 0) {
    lockedSecurityGroups = hasAutomaticPort
      ? // Mixed case (some specific ports, some automatic): lock only the groups that
        // come from the ports, and leave the default group removable.
        securityGroupsList.filter((sg) => portSgIds.has(sg.id) && sg.name !== 'default')
      : // Every NIC uses a specific port: lock all the pre-selected groups.
        autoSelectedGroups
  }

  const tooltipMsg =
    hasSelectedPortWithSGs && portSgIds.size > 0
      ? 'Security groups are enforced by the selected port(s) and cannot be removed'
      : 'Security groups are required because port security is enabled on the selected network(s). You can choose additional security groups, or the default security group will be applied automatically.'

  return {
    toggleState: 'lockedOn',
    tooltipMsg,
    autoSelectedGroups,
    lockedSecurityGroups,
    hasPortSecurityGroups: hasSelectedPortWithSGs,
  }
}

export const securityGroupColumns = [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'description',
    label: 'Description',
  },
]

export const volumeSnapshotColumns = [
  {
    id: 'name',
    label: 'name',
  },
  {
    id: 'description',
    label: 'Description',
  },
  {
    id: 'status',
    label: 'Status',
  },
  {
    id: 'size',
    label: 'Size',
    render: (size) => humanReadableSize(size * 1024 * 1024 * 1024),
  },
]

export const convertToBytes = (value: string | number, unit: 'GB' | 'MB' | 'KB' | 'B') => {
  const val = Number(value)
  switch (unit) {
    case 'GB':
      return val * 1024 * 1024 * 1024
    case 'MB':
      return val * 1024 * 1024
    case 'KB':
      return val * 1024
    case 'B':
      return val
  }
}

export const isFlavorCompatibleWithImageAndVolume = (flavor, params) => {
  const imageSize = params.image?.[0]?.virtual_size
  const volumeSize = convertToBytes(params?.volumeSize, 'GB') || 0
  const flavorSize = flavor?.disk >= 0 ? convertToBytes(flavor?.disk, 'GB') || 0 : 0

  // Flavor must be larger than the image size
  const isCompatibleFlavor =
    flavorSize === 0 || params?.sourceType === 'newVolume' || !imageSize || flavorSize >= imageSize
  return !isCompatibleFlavor
}
export const isImageCompatibleWithVolumeAndFlavor = (image, params) => {
  const imageSize = image?.virtual_size
  const volumeSize = convertToBytes(params?.volumeSize, 'GB')
  const flavorSize = convertToBytes(params?.flavor[0]?.disk, 'GB') || null

  // imageValid: is the flavor's disk at least as big as the image? (For the "new volume" source we
  // instead require the new volume to be at least the image size — see the next line.)
  const imageValid = flavorSize === null ? true : flavorSize >= 0 ? flavorSize >= imageSize : true
  const disabled = params?.sourceType === 'newVolume' ? imageSize > volumeSize : !imageValid
  return disabled
}

export const filterVmsByRole = (userRole, userId, allTenants, allOwners, vms, allVms) => {
  switch (userRole) {
    case 'admin':
      return allTenants ? allVms : vms
    case 'member':
      if (!allOwners) {
        return vms?.filter((vm) => vm?.user_id === userId)
      }
      return vms
    case 'reader':
      return vms
    default:
      return vms
  }
}

export const pageSizeOptions = [3, 5, 10].map((value) => ({ value }))

export const formatTimestampToLocal = (time: string): string => {
  if (!time) return '-' // Handle empty or undefined input
  return moment
    .utc(time)
    .local() // Convert the UTC time to the browser/system's local timezone.
    .format('LL LTS')
}

export const closeOnEscape = (onClose: () => void) => {
  const handleEscapeKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose()
    }
  }

  window.addEventListener('keydown', handleEscapeKeyPress)
  return () => {
    window.removeEventListener('keydown', handleEscapeKeyPress)
  }
}

// True if the VM boots from a volume — that is, one of its volumes is the boot disk
// (boot index 0) rather than booting from an image.
export const isVolumeBackedVm = (vm) => {
  const volumeKeys = keys(vm?.volumeDetails)
  return volumeKeys?.some((key) => {
    const volume = vm?.volumeDetails?.[key]
    return volume?.boot_index === 0
  })
}

// VirtualMachinesList FR-037: A VM is hotplug-enabled when it carries the HOTPLUG_MEMORY
// metadata key (written at create/clone time). Hotplug VMs use the Hotplug action to change
// resources; Resize/Confirm Resize are not applicable to them.
export const isHotplugVm = (vm) => {
  return !!vm?.metadata?.['HOTPLUG_MEMORY']
}

// VirtualMachinesList/VmDetailsPage FR (PCD-7855): gates the Resize/Confirm
// Resize/Hotplug actions consistently in both the batch-action menu (array of selected VMs)
// and the single-VM details page. Extracted from nested-ternary disabledTooltip/tooltip
// callbacks so the gating logic is unit-testable independent of rendering the menu/page.
export const canResizeVm = (vm): boolean => vm?.state !== 'resized' && !isHotplugVm(vm)

export const getResizeVmTooltip = (vm): string => {
  if (isHotplugVm(vm)) return ACTION_LIMITATION_TOOLTIPS.RESIZE_HOTPLUG_VM
  if (vm?.state === 'resized') return ACTION_LIMITATION_TOOLTIPS.ALREADY_RESIZED
  return ''
}

export const canResizeVms = (selected): boolean =>
  selected?.length === 1 && canResizeVm(selected[0])

export const getResizeVmsTooltip = (selected): string => {
  if (selected?.length > 1) return ACTION_LIMITATION_TOOLTIPS.RESIZE_VM
  return getResizeVmTooltip(selected?.[0])
}

// Confirm Resize stays available for hotplug VMs: they can't START a resize (canResizeVm),
// but one already sitting in verify_resize (resized via CLI or before hotplug gating shipped)
// must have a UI path to confirm — otherwise it is stranded mid-resize.
export const canConfirmResizeVm = (vm): boolean => vm?.state === 'resized'

export const getConfirmResizeVmTooltip = (vm): string => {
  if (vm?.state !== 'resized') return ACTION_LIMITATION_TOOLTIPS.NOT_RESIZED
  return ''
}

export const canConfirmResizeVms = (selected): boolean =>
  selected?.length === 1 && canConfirmResizeVm(selected[0])

export const getConfirmResizeVmsTooltip = (selected): string => {
  if (selected?.length > 1) return ACTION_LIMITATION_TOOLTIPS.CONFIRM_RESIZE_VM
  return getConfirmResizeVmTooltip(selected?.[0])
}

export const canHotplugVm = (vm): boolean =>
  isHotplugVm(vm) && ['active', 'stopped'].includes(vm?.state)

export const getHotplugVmTooltip = (vm): string => {
  if (!isHotplugVm(vm)) return ACTION_LIMITATION_TOOLTIPS.HOTPLUG_ENABLED
  if (!['active', 'stopped'].includes(vm?.state))
    return ACTION_LIMITATION_TOOLTIPS.DISABLED_HOTPLUG_VM_STATE
  return ''
}

export const canHotplugVms = (selected): boolean =>
  selected?.length === 1 && canHotplugVm(selected[0])

export const getHotplugVmsTooltip = (selected): string => {
  if (selected?.length > 1) return ACTION_LIMITATION_TOOLTIPS.HOTPLUG_ONE_VM
  return getHotplugVmTooltip(selected?.[0])
}

export const canSoftReboot = (vm) => {
  return vm?.state === 'active'
}

export const canHardReboot = (vm) => {
  const validStates = ['active', 'error', 'hard_reboot', 'paused', 'reboot', 'shutoff', 'suspended']
  return validStates.includes(vm?.state)
}

export const canChangeTenantVms = (userRole, vms): boolean => {
  if (!vms || vms?.length === 0) return false
  return (
    ['admin', 'platform_admin'].includes(userRole) &&
    vms.every((vm) => CHANGE_TENANT_VALID_STATES.includes(vm?.state))
  )
}

// A VM's host is treated as unusable when the resmgr reports it is not
// responding, or when the host record is gone entirely.
//
// Host liveness comes from two sources and we deliberately prefer the fresher
// one. The `/vmslist` payload embeds a per-VM `hostDetails` snapshot that is
// captured when the page loads and goes stale the moment a host drops offline.
// The resmgr `hosts` list (the same source VmStateCellComponent uses to render
// the "(host offline)" badge) stays current. When a fresh `hosts` list is
// supplied we look the VM's host up by its `OS-EXT-SRV-ATTR:host` and, if we
// find it, trust its `info.responding` over the stale snapshot. If the host is
// NOT in the list (e.g. the list is still loading, scoped, or the host was
// deleted) we fall back to the embedded `hostDetails` rather than guessing, so a
// healthy VM is never made deletable just because its host is momentarily absent
// from the list. A VM whose host is offline or gone can never be stopped, so the
// "stop before delete" guard below must not permanently trap it. See
// helpers.spec.md CDV-FR-003.
const isVmHostOfflineOrMissing = (vm, hosts?: any[]) => {
  // If the VM or the host list is missing, we can't determine the host's status.
  if (!vm || !hosts?.length) return false

  const host = hosts?.find((h) => h.id === vm?.['OS-EXT-SRV-ATTR:host'])
  if (host) {
    return host?.info?.responding === false
  }
  return vm?.hostDetails?.info?.responding === false || !vm?.hostDetails?.id
}

export const canDeleteVms = (vms, hosts?: any[]) => {
  if (!vms || vms?.length === 0) return false

  const disallowedStates = ['active', 'paused', 'suspended']
  return vms?.every((vm) => {
    if (vm?.state == null) return false
    // CDV-FR-001: any other state is deletable as before.
    if (!disallowedStates.includes(vm.state)) return true
    // CDV-FR-002 / CDV-FR-003: an active/paused/suspended VM stays blocked so
    // the user stops it first, unless its host is offline or gone, in which
    // case stopping is impossible and we allow the delete.
    return isVmHostOfflineOrMissing(vm, hosts)
  })
}

export const getExtraDriveVolumesInfo = (vm: any): ExtraDriveInfo => {
  // Filter volumes that represent "extra" drives (not the primary OS disk)
  const volumes = Object.values(vm?.volumeDetails || {}).filter((vol: any) => {
    // Condition 1: Was it hot-plugged later with our specific API tag?
    const hasExtraDriveTag = vol.tag === 'extra_drive'

    // Condition 2: Was it attached during VM creation as a virtual CD-ROM?
    const isCdrom = vol.device_type === 'cdrom'

    // Ensure we never accidentally unmount the primary operating system drive
    const isNotPrimaryBoot = vol.boot_index !== 0

    // An extra drive is EITHER explicitly tagged OR it is a non-primary CD-ROM
    return hasExtraDriveTag || (isCdrom && isNotPrimaryBoot)
  })

  // Return both the array and derived properties to avoid recomputation
  return {
    volumes, // The actual extra drive volume objects
    hasExtraDrives: volumes.length > 0, // Quick boolean check
    count: volumes.length, // How many extra drives are attached
  }
}
